/**
 * Password Reset Modal - Email-only forgot password flow
 * Sends password reset link to email address
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import Button from './Button';
import { FormInput } from './FormInput';
import { X, AlertTriangle, Mail } from 'lucide-react';
import '../styles/Modal.css';

const PasswordResetModal = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const modalRef = useRef();

  // Local state - isolated from parent component
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Modal behavior (focus trap, ESC key, etc.)
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // ESC key handler
      const handleEscape = (e) => {
        if (e.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, onClose]);

  // Memoized onChange handlers to prevent FormInput from seeing new function references
  const handleEmailChange = useCallback((e) => {
    setResetEmail(e.target.value);
  }, []);

  const handleClose = () => {
    // Reset all state when closing
    setResetEmail('');
    setError('');
    setLoading(false);
    setEmailSent(false);
    onClose();
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(resetEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/password-reset/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: resetEmail
        }),
      });

      if (response.ok) {
        setEmailSent(true);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to send reset email');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Inline styles for the modal (self-contained, no external Modal component)
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.colors.background + 'E6',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    content: {
      background: theme.colors.surface,
      color: theme.colors.textPrimary,
      borderRadius: theme.borderRadius.lg,
      boxShadow: theme.shadows.xl,
      maxWidth: '500px',
      width: '90%',
      maxHeight: '90vh',
      overflow: 'auto'
    },
    header: {
      borderBottom: `1px solid ${theme.colors.inputBorder}`,
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      color: theme.colors.textPrimary,
      fontSize: theme.typography.sizes['2xl'],
      fontWeight: theme.typography.weights.semibold,
      margin: 0
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: theme.colors.textSecondary,
      fontSize: theme.typography.sizes.xl,
      cursor: 'pointer',
      padding: '0.5rem',
      lineHeight: 1
    },
    body: {
      padding: theme.spacing.xl
    }
  };

  return (
    <div
      style={modalStyles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div ref={modalRef} style={modalStyles.content}>
        <div style={modalStyles.header}>
          <h2 id="modal-title" style={modalStyles.title}>Reset Your Password</h2>
          <button
            style={modalStyles.closeButton}
            onClick={handleClose}
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>
        <div style={modalStyles.body}>
          {!emailSent ? (
            <>
              <p style={{ marginBottom: '1.5rem', color: theme.colors.textSecondary, fontSize: '0.95rem' }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {error && (
                <div style={{
                  padding: '1rem',
                  marginBottom: '1rem',
                  backgroundColor: 'var(--color-error-light)',
                  border: '1px solid var(--color-error)',
                  borderRadius: '4px',
                  color: 'var(--color-error)'
                }}>
                  <span aria-hidden="true"><AlertTriangle size={16} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'text-bottom' }} /></span> {error}
                </div>
              )}

              <FormInput
                label="Email Address"
                type="email"
                name="resetEmail"
                value={resetEmail}
                onChange={handleEmailChange}
                placeholder="your@email.com"
                required
                variant="light"
              />

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <Button
                  variant="outline"
                  theme="light"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleForgotPassword}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </>
          ) : (
            // Success message
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                <Mail size={48} color={theme.colors.accent} />
              </div>
              <h3 style={{ 
                marginBottom: '0.5rem', 
                fontSize: '1.5rem',
                color: theme.colors.textPrimary
              }}>
                Check Your Email
              </h3>
              <p style={{ color: theme.colors.textSecondary, marginBottom: '1rem' }}>
                We've sent a password reset link to:
              </p>
              <p style={{ 
                fontWeight: 'bold',
                color: theme.colors.textPrimary,
                marginBottom: '1.5rem',
                wordBreak: 'break-all'
              }}>
                {resetEmail}
              </p>
              <p style={{ color: theme.colors.textSecondary, fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                The link will expire in 30 minutes. If you don't see the email, check your spam folder.
              </p>
              <Button
                variant="primary"
                onClick={handleClose}
                fullWidth
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetModal;